import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Users, Plus, Trash2, Shield, Eye, Loader2, AlertCircle, XCircle, CheckCircle } from 'lucide-react';

const ROLE_OPTIONS = [
  { value: 'viewer', label: 'Viewer', description: 'Can view analytics and visitor data' },
  { value: 'admin', label: 'Admin', description: 'Can manage site settings and users' },
];

const ROLE_COLORS = {
  owner: 'bg-purple-100 text-purple-700',
  admin: 'bg-blue-100 text-blue-700',
  viewer: 'bg-gray-100 text-gray-700',
};

const TrackedSiteUserManagement = ({
  siteId,
  siteName,
  currentUserEmail,
  onClose,
  fetchSiteUsers,
  addSiteUser,
  removeSiteUser,
}) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserRole, setNewUserRole] = useState('viewer');
  const [actionLoading, setActionLoading] = useState(null);
  const [addError, setAddError] = useState(null);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchSiteUsers(siteId);
      setUsers(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [fetchSiteUsers, siteId]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const handleAddUser = async (e) => {
    e.preventDefault();
    if (!newUserEmail.trim()) return;

    setActionLoading('add');
    setAddError(null);
    try {
      await addSiteUser(siteId, newUserEmail, newUserRole);
      setNewUserEmail('');
      setNewUserRole('viewer');
      setShowAddForm(false);
      await loadUsers();
    } catch (err) {
      setAddError(err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleRemoveUser = async (userId, userEmail) => {
    if (userEmail === currentUserEmail) return; // Can't remove yourself
    setActionLoading(userId);
    try {
      await removeSiteUser(userId);
      await loadUsers();
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-lg max-w-lg w-full max-h-[80vh] overflow-y-auto">
      {/* Header */}
      <div className="flex items-center justify-between p-5 border-b border-gray-200">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Users className="w-5 h-5 text-primary-600" />
            Site Users
          </h3>
          <p className="text-sm text-gray-500 mt-0.5">{siteName}</p>
        </div>
        <button
          onClick={onClose}
          className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <XCircle className="w-5 h-5" />
        </button>
      </div>

      {/* Content */}
      <div className="p-5 space-y-4">
        {error && (
          <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 p-3 rounded-lg">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            {error}
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
          </div>
        ) : (
          <>
            {/* Users List */}
            <div className="space-y-2">
              {users.map(user => (
                <div
                  key={user.id}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                      <span className="text-xs font-bold text-primary-700">
                        {user.user_email?.charAt(0)?.toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">{user.user_email}</p>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${ROLE_COLORS[user.role] || ROLE_COLORS.viewer}`}>
                        {user.role === 'owner' && <Shield className="w-3 h-3 mr-1" />}
                        {user.role === 'viewer' && <Eye className="w-3 h-3 mr-1" />}
                        {user.role}
                      </span>
                    </div>
                  </div>

                  {user.role !== 'owner' && user.user_email !== currentUserEmail && (
                    <button
                      onClick={() => handleRemoveUser(user.id, user.user_email)}
                      disabled={actionLoading === user.id}
                      className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                      title="Remove user"
                    >
                      {actionLoading === user.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Trash2 className="w-4 h-4" />
                      )}
                    </button>
                  )}
                </div>
              ))}
            </div>

            {/* Add User Form */}
            {showAddForm ? (
              <motion.form
                initial={{ opacity: 0, y: -5 }}
                animate={{ opacity: 1, y: 0 }}
                onSubmit={handleAddUser}
                className="space-y-3 p-4 bg-blue-50 border border-blue-200 rounded-lg"
              >
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    value={newUserEmail}
                    onChange={(e) => setNewUserEmail(e.target.value)}
                    placeholder="user@example.com"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                  <select
                    value={newUserRole}
                    onChange={(e) => setNewUserRole(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    {ROLE_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label} — {opt.description}</option>
                    ))}
                  </select>
                </div>

                {addError && (
                  <div className="flex items-center gap-2 text-red-600 text-sm">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    {addError}
                  </div>
                )}

                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => { setShowAddForm(false); setAddError(null); }}
                    className="px-3 py-1.5 text-gray-600 text-sm hover:text-gray-800"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={actionLoading === 'add'}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-primary-600 text-white text-sm rounded-lg hover:bg-primary-700 disabled:opacity-50"
                  >
                    {actionLoading === 'add' ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <CheckCircle className="w-3.5 h-3.5" />
                    )}
                    Add User
                  </button>
                </div>
              </motion.form>
            ) : (
              <button
                onClick={() => setShowAddForm(true)}
                className="flex items-center gap-2 w-full px-4 py-2.5 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 hover:text-primary-600 hover:border-primary-400 transition-colors text-sm"
              >
                <Plus className="w-4 h-4" />
                Add User
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default TrackedSiteUserManagement;
